import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ScrollText } from 'lucide-react';
import { Button } from '@/components/ui/button';

const TermsPage = () => {
  const navigate = useNavigate();
  const LAST_UPDATED = 'February 23, 2026';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50" data-testid="terms-page">
      {/* Header */}
      <header className="glass-card border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
            data-testid="terms-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
          <div className="flex items-center gap-3">
            <img src="/tabedlab-logo.png" alt="TabedLab Logo" className="w-10 h-10 object-contain" />
            <h1 className="text-xl font-bold text-slate-800">TabedLab</h1>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="glass-card rounded-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <ScrollText className="w-8 h-8 text-blue-600" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 mb-4">Terms of Service</h1>
            <p className="text-slate-600">Last Updated: {LAST_UPDATED}</p>
          </div>

          <div className="space-y-8 text-slate-700 leading-relaxed">
            <section>
              <p className="text-lg">
                Welcome to TabedLab. By accessing or using our spectral and chromatographic analysis platform
                (the "Service"), you agree to be bound by these Terms of Service ("Terms"). Please read them
                carefully. If you do not agree with any part of the Terms, you may not use the Service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">1. Use of the Service</h2>
              <p>
                TabedLab provides AI-assisted analysis of analytical chemistry data (FTIR spectroscopy and HPLC chromatography). You may use the Service for lawful scientific, educational, or
                commercial purposes, subject to these Terms and your subscription plan.
              </p>
              <p className="mt-2">
                You agree not to: (a) reverse-engineer, decompile, or otherwise attempt to derive the source
                code of the Service; (b) upload malicious, infringing, or unlawful content; (c) use the Service
                to mislead, defraud, or harm any third party; (d) attempt to bypass rate limits, authentication,
                or access controls.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">2. Accounts &amp; Security</h2>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for
                all activity that occurs under your account. Notify us immediately at{' '}
                <a href="mailto:contact@tabedlab.com" className="text-blue-600 hover:underline">
                  contact@tabedlab.com
                </a>{' '}
                of any unauthorized use.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">3. Data &amp; Intellectual Property</h2>
              <p>
                You retain ownership of the data files you upload. By using the Service, you grant TabedLab a
                limited, non-exclusive license to process your data solely to provide the analysis results you
                request. We do <strong>not</strong> use your uploaded data to train AI models. See our{' '}
                <a href="/privacy-policy" className="text-blue-600 hover:underline">Privacy Policy</a> for
                details.
              </p>
              <p className="mt-2">
                All software, designs, documentation and trademarks associated with TabedLab remain the
                property of TabedLab and its licensors.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">4. Subscriptions &amp; Payment</h2>
              <p>
                Paid subscriptions (Student, Research, Lab) are billed in advance through Stripe or PayPal,
                in accordance with the plan you select. Subscriptions renew automatically unless cancelled
                before the next billing cycle. Refunds are evaluated on a case-by-case basis.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">5. Analytical Accuracy &amp; Disclaimer</h2>
              <p>
                TabedLab's AI-generated interpretations, peak assignments, and database matches are provided
                <strong> as analytical aids only</strong>. They are not a substitute for expert review,
                validated reference methods, or regulatory-grade analytical procedures. You are responsible
                for verifying the accuracy and applicability of any result before relying on it for research,
                publication, medical, regulatory, or commercial decisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">6. Limitation of Liability</h2>
              <p>
                To the maximum extent permitted by law, TabedLab and its affiliates are not liable for any
                indirect, incidental, special, consequential or punitive damages arising from your use of, or
                inability to use, the Service. Our total liability for any claim arising out of these Terms is
                limited to the amount you have paid to TabedLab in the twelve (12) months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">7. Termination</h2>
              <p>
                We may suspend or terminate your access to the Service if you breach these Terms or if we
                are required to do so by law. You may close your account at any time by contacting us.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">8. Changes to the Terms</h2>
              <p>
                We may update these Terms from time to time. Material changes will be communicated by email
                or via a notice on the platform. Continued use of the Service after changes take effect
                constitutes acceptance of the revised Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">9. Governing Law</h2>
              <p>
                These Terms are governed by the laws applicable to the location of TabedLab's principal place
                of business, without regard to its conflict-of-law provisions. Any dispute will be resolved
                through good-faith negotiation, and, if unresolved, in the competent courts of that
                jurisdiction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-slate-900 mb-3">10. Contact</h2>
              <p>
                Questions about these Terms? Reach us at{' '}
                <a href="mailto:contact@tabedlab.com" className="text-blue-600 hover:underline">
                  contact@tabedlab.com
                </a>{' '}
                or through our <a href="/contact" className="text-blue-600 hover:underline">contact page</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
